<template>
  <div id="app" class="container-fluid">
    <!-- Header with Patient Info -->
    <full_head />

    <!-- Main Content -->
    <div class="main-content">
      <div class="weight-monitoring-section">
        <!-- Summary Stats -->
        <div class="summary-stats">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="fas fa-weight-scale"></i>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ currentWeight }} kg</div>
              <div class="stat-label">Current Weight</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon">
              <i class="fas fa-chart-line"></i>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ totalGain }} kg</div>
              <div class="stat-label">Total Weight Gain</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon">
              <i class="fas fa-calendar-alt"></i>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ weightRecords.length }}</div>
              <div class="stat-label">Records</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon">
              <i class="fas fa-baby"></i>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ currentGestation }} wks</div>
              <div class="stat-label">Current Gestation</div>
            </div>
          </div>
        </div>

        <!-- Weight Chart -->
        <div class="card">
          <div class="card-header">
            <h3><i class="fas fa-chart-line"></i> Weight Monitoring Chart</h3>
            <div class="chart-controls">
              <button
                class="btn-chart"
                @click="chartType = 'line'"
                :class="{ active: chartType === 'line' }"
              >
                <i class="fas fa-chart-line"></i> Line
              </button>
              <button
                class="btn-chart"
                @click="chartType = 'bar'"
                :class="{ active: chartType === 'bar' }"
              >
                <i class="fas fa-chart-bar"></i> Bar
              </button>
            </div>
          </div>
          <div class="card-body">
            <div class="chart-container">
              <canvas ref="weightChart"></canvas>
            </div>
            <div class="chart-legend">
              <div class="legend-item">
                <span
                  class="legend-color"
                  style="background-color: #4a90e2"
                ></span>
                <span>Actual Weight (kg)</span>
              </div>
              <div class="legend-item">
                <span
                  class="legend-color"
                  style="background-color: #50e3c2"
                ></span>
                <span>Expected Weight (kg)</span>
              </div>
              <div class="legend-item">
                <span
                  class="legend-color"
                  style="background-color: #f5a623"
                ></span>
                <span>Recommended Range</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Weight Records Table -->
        <div class="card">
          <div class="card-header">
            <h3><i class="fas fa-table"></i> Weight Records</h3>
            <button class="btn-add" @click="showAddRecordModal = true">
              <i class="fas fa-plus"></i> Add Record
            </button>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table">
                <thead>
                  <tr>
                    <th>Visit Date</th>
                    <th>Gestation (Weeks)</th>
                    <th>Weight (kg)</th>
                    <th>Change (kg)</th>
                    <th>BMI</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(record) in weightRecords" :key="record.id">
                    <td>{{ formatDate(record.date) }}</td>
                    <td>{{ record.gestation }} wks</td>
                    <td class="weight-value">{{ record.weight }} kg</td>
                    <td :class="getChangeClass(record.change)">
                      <i v-if="record.change > 0" class="fas fa-arrow-up"></i>
                      <i
                        v-else-if="record.change < 0"
                        class="fas fa-arrow-down"
                      ></i>
                      {{ record.change > 0 ? "+" : "" }}{{ record.change }} kg
                    </td>
                    <td>{{ record.bmi.toFixed(1) }}</td>
                    <td>{{ record.notes || "-" }}</td>
                    <td class="actions">
                      <button
                        class="btn-action edit"
                        @click="editRecord(record)"
                      >
                        <i class="fas fa-edit"></i>
                      </button>
                      <button
                        class="btn-action delete"
                        @click="deleteRecord(record.id)"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- BMI Guidelines -->
        <div class="card">
          <div class="card-header">
            <h3>
              <i class="fas fa-info-circle"></i> Pregnancy Weight Gain
              Guidelines
            </h3>
          </div>
          <div class="card-body">
            <div class="guidelines-grid">
              <div class="guideline-item">
                <div class="guideline-header underweight">
                  <h4>Underweight (BMI = 18.5)</h4>
                </div>
                <div class="guideline-content">
                  <div class="recommended-gain">12.5 - 18 kg</div>
                  <div class="rate">0.5 kg/week (2nd & 3rd trimester)</div>
                </div>
              </div>

              <div class="guideline-item">
                <div class="guideline-header normal">
                  <h4>Normal Weight (BMI 18.5-24.9)</h4>
                </div>
                <div class="guideline-content">
                  <div class="recommended-gain">11.5 - 16 kg</div>
                  <div class="rate">0.4 kg/week (2nd & 3rd trimester)</div>
                </div>
              </div>

              <div class="guideline-item">
                <div class="guideline-header overweight">
                  <h4>Overweight (BMI 25-29.9)</h4>
                </div>
                <div class="guideline-content">
                  <div class="recommended-gain">7 - 11.5 kg</div>
                  <div class="rate">0.3 kg/week (2nd & 3rd trimester)</div>
                </div>
              </div>

              <div class="guideline-item">
                <div class="guideline-header obese">
                  <h4>Obese (BMI ≥ 30)</h4>
                </div>
                <div class="guideline-content">
                  <div class="recommended-gain">5 - 9 kg</div>
                  <div class="rate">0.2 kg/week (2nd & 3rd trimester)</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Record Modal -->
    <div v-if="showAddRecordModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingRecord ? "Edit" : "Add" }} Weight Record</h3>
          <button class="modal-close" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveRecord">
            <div class="form-grid">
              <div class="form-group">
                <label>Date</label>
                <input
                  type="date"
                  v-model="form.date"
                  required
                  class="form-control"
                />
              </div>

              <div class="form-group">
                <label>Gestation (weeks)</label>
                <input
                  type="number"
                  v-model="form.gestation"
                  min="4"
                  max="42"
                  required
                  class="form-control"
                />
              </div>

              <div class="form-group">
                <label>Weight (kg)</label>
                <input
                  type="number"
                  v-model="form.weight"
                  min="30"
                  max="150"
                  step="0.1"
                  required
                  class="form-control"
                />
              </div>

              <div class="form-group">
                <label>Height (cm)</label>
                <input
                  type="number"
                  v-model="form.height"
                  min="120"
                  max="220"
                  required
                  class="form-control"
                />
              </div>

              <div class="form-group full-width">
                <label>Notes</label>
                <textarea
                  v-model="form.notes"
                  class="form-control"
                  rows="3"
                ></textarea>
              </div>
            </div>

            <div class="form-preview">
              <div class="preview-item">
                <span>BMI:</span>
                <strong>{{ calculateBMI(form.weight, form.height) }}</strong>
              </div>
              <div class="preview-item">
                <span>Category:</span>
                <strong>{{
                  getBMICategory(calculateBMI(form.weight, form.height))
                }}</strong>
              </div>
            </div>

            <div class="modal-actions">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeModal"
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">
                {{ editingRecord ? "Update" : "Save" }} Record
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import full_head from "./full_head.vue";
import { Chart, registerables } from "chart.js";
Chart.register(...registerables);

export default {
  name: "ANCWeightMonitoring",
  components: {
    full_head,
  },
  data() {
    return {
      chartType: "line",
      weightChart: null,
      showAddRecordModal: false,
      editingRecord: null,
      form: {
        date: new Date().toISOString().split("T")[0],
        gestation: 12,
        weight: 60,
        height: 165,
        notes: "",
      },
      currentGestation: 28,
      currentWeight: 68.5,
      totalGain: 8.5,
      weightRecords: [
        {
          id: 1,
          date: "2023-09-01",
          gestation: 12,
          weight: 60.0,
          height: 165,
          change: 0,
          bmi: 22.0,
          notes: "Initial visit",
        },
        {
          id: 2,
          date: "2023-10-05",
          gestation: 16,
          weight: 61.5,
          height: 165,
          change: 1.5,
          bmi: 22.6,
          notes: "Normal progress",
        },
        {
          id: 3,
          date: "2023-11-10",
          gestation: 20,
          weight: 63.0,
          height: 165,
          change: 1.5,
          bmi: 23.1,
          notes: "Good weight gain",
        },
        {
          id: 4,
          date: "2023-12-08",
          gestation: 24,
          weight: 65.0,
          height: 165,
          change: 2.0,
          bmi: 23.9,
          notes: "Steady increase",
        },
        {
          id: 5,
          date: "2024-01-12",
          gestation: 28,
          weight: 68.5,
          height: 165,
          change: 3.5,
          bmi: 25.2,
          notes: "Within expected range",
        },
      ],
    };
  },
  mounted() {
    this.renderChart();
    this.calculateChanges();
  },
  methods: {
    calculateChanges() {
      for (let i = 1; i < this.weightRecords.length; i++) {
        this.weightRecords[i].change =
          this.weightRecords[i].weight - this.weightRecords[i - 1].weight;
      }
      this.currentWeight = this.weightRecords[
        this.weightRecords.length - 1
      ].weight;
      this.totalGain = this.currentWeight - this.weightRecords[0].weight;
    },

    calculateBMI(weight, height) {
      if (!weight || !height) return 0;
      const heightM = height / 100;
      return (weight / (heightM * heightM)).toFixed(1);
    },

    getBMICategory(bmi) {
      if (bmi < 18.5) return "Underweight";
      if (bmi < 25) return "Normal";
      if (bmi < 30) return "Overweight";
      return "Obese";
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    getChangeClass(change) {
      if (change > 2) return "change-high";
      if (change > 0.5) return "change-normal";
      if (change < 0) return "change-low";
      return "change-stable";
    },

    editRecord(record) {
      this.editingRecord = record;
      this.form = { ...record };
      this.showAddRecordModal = true;
    },

    deleteRecord(id) {
      if (confirm("Are you sure you want to delete this record?")) {
        this.weightRecords = this.weightRecords.filter(
          (record) => record.id !== id
        );
        this.calculateChanges();
        this.renderChart();
      }
    },

    closeModal() {
      this.showAddRecordModal = false;
      this.editingRecord = null;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        date: new Date().toISOString().split("T")[0],
        gestation: this.currentGestation + 4,
        weight: this.currentWeight,
        height: 165,
        notes: "",
      };
    },

    saveRecord() {
      const bmi = parseFloat(
        this.calculateBMI(this.form.weight, this.form.height)
      );

      if (this.editingRecord) {
        // Update existing record
        const index = this.weightRecords.findIndex(
          (r) => r.id === this.editingRecord.id
        );
        const change =
          index > 0
            ? this.form.weight - this.weightRecords[index - 1].weight
            : 0;

        this.weightRecords[index] = {
          ...this.form,
          id: this.editingRecord.id,
          bmi,
          change,
        };
      } else {
        // Add new record
        const change =
          this.weightRecords.length > 0
            ? this.form.weight -
              this.weightRecords[this.weightRecords.length - 1].weight
            : 0;

        const newRecord = {
          ...this.form,
          id: Date.now(),
          bmi,
          change,
        };
        this.weightRecords.push(newRecord);
      }

      // Sort by date
      this.weightRecords.sort((a, b) => new Date(a.date) - new Date(b.date));

      this.calculateChanges();
      this.renderChart();
      this.closeModal();
    },

    renderChart() {
      if (this.weightChart) {
        this.weightChart.destroy();
      }

      const ctx = this.$refs.weightChart.getContext("2d");
      const gestationWeeks = this.weightRecords.map((r) => r.gestation);
      const actualWeights = this.weightRecords.map((r) => r.weight);

      // Calculate expected weight based on normal pregnancy weight gain
      const initialWeight = this.weightRecords[0].weight;
      const initialBMI = this.weightRecords[0].bmi;
      const expectedWeights = gestationWeeks.map((week) => {
        const weeksFromStart = week - this.weightRecords[0].gestation;
        let weeklyGain = 0.4; // Normal weight default

        if (initialBMI < 18.5) weeklyGain = 0.5;
        else if (initialBMI >= 25 && initialBMI < 30) weeklyGain = 0.3;
        else if (initialBMI >= 30) weeklyGain = 0.2;

        return initialWeight + weeklyGain * weeksFromStart;
      });

      this.weightChart = new Chart(ctx, {
        type: this.chartType,
        data: {
          labels: gestationWeeks.map((w) => `${w} wks`),
          datasets: [
            {
              label: "Actual Weight",
              data: actualWeights,
              borderColor: "#4A90E2",
              backgroundColor:
                this.chartType === "bar" ? "#4A90E2" : "transparent",
              borderWidth: 3,
              fill: false,
              tension: 0.3,
            },
            {
              label: "Expected Weight",
              data: expectedWeights,
              borderColor: "#50E3C2",
              backgroundColor:
                this.chartType === "bar" ? "#50E3C2" : "transparent",
              borderWidth: 2,
              borderDash: [5, 5],
              fill: false,
              tension: 0.3,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  return `${context.dataset.label}: ${context.raw} kg`;
                },
              },
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: "Gestation (Weeks)",
              },
            },
            y: {
              title: {
                display: true,
                text: "Weight (kg)",
              },
              min: Math.min(...actualWeights) - 5,
              max: Math.max(...actualWeights) + 5,
            },
          },
        },
      });
    },
  },
  watch: {
    chartType() {
      this.renderChart();
    },
  },
};
</script>

<style scoped>
.container-fluid {
  padding: 20px;
  background-color: #f8f9fa;
}

.main-content {
  margin-top: 20px;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.stat-card {
  background: white;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 20px;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 5px;
}

.stat-label {
  color: #7f8c8d;
  font-size: 14px;
  font-weight: 600;
}

.card {
  background: white;
  border-radius: 10px;
  margin-bottom: 25px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  border: none;
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px;
  border-bottom: 2px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  color: #495057;
  font-size: 20px;
  font-weight: 600;
}

.card-header h3 i {
  margin-right: 10px;
  color: #667eea;
}

.chart-controls {
  display: flex;
  gap: 10px;
}

.btn-chart {
  padding: 8px 15px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
}

.btn-chart.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.btn-add {
  padding: 8px 20px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
}

.card-body {
  padding: 25px;
}

.chart-container {
  height: 400px;
  position: relative;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 20px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.legend-color {
  width: 20px;
  height: 5px;
  border-radius: 2px;
}

.table-responsive {
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th {
  background: #f8f9fa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
}

.table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
}

.weight-value {
  font-weight: 600;
  color: #2c3e50;
}

.change-high {
  color: #e74c3c;
  font-weight: 600;
}

.change-normal {
  color: #27ae60;
  font-weight: 600;
}

.change-low {
  color: #f39c12;
  font-weight: 600;
}

.change-stable {
  color: #7f8c8d;
  font-weight: 600;
}

.actions {
  display: flex;
  gap: 10px;
}

.btn-action {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-action.edit {
  background: #3498db;
  color: white;
}

.btn-action.delete {
  background: #e74c3c;
  color: white;
}

.guidelines-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.guideline-item {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.guideline-header {
  padding: 15px;
  color: white;
  text-align: center;
}

.guideline-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.guideline-header.underweight {
  background: #3498db;
}

.guideline-header.normal {
  background: #27ae60;
}

.guideline-header.overweight {
  background: #f39c12;
}

.guideline-header.obese {
  background: #e74c3c;
}

.guideline-content {
  padding: 15px;
  background: white;
  text-align: center;
}

.recommended-gain {
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 10px;
}

.rate {
  color: #7f8c8d;
  font-size: 14px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 10px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
}

.modal-body {
  padding: 30px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #495057;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
}

.form-preview {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-around;
}

.preview-item {
  text-align: center;
}

.preview-item span {
  display: block;
  color: #7f8c8d;
  font-size: 14px;
  margin-bottom: 5px;
}

.preview-item strong {
  font-size: 18px;
  color: #2c3e50;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
}

.btn {
  padding: 10px 25px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-primary {
  background: #667eea;
  color: white;
}

@media (max-width: 768px) {
  .summary-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .guidelines-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .container-fluid {
    padding: 10px;
  }

  .summary-stats {
    grid-template-columns: 1fr;
  }

  .card-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .chart-legend {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .chart-container {
    height: 300px;
  }
}
</style>
